import { useState, useEffect } from 'react';
import { Search, Download, ArrowRight, Filter, Calendar, Plus, Mail, DollarSign, Eye, FileText } from 'lucide-react';
import Header from '../../components/layout/Header';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useToast } from '../../components/ui/use-toast';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious
} from '../../components/ui/pagination';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../components/ui/dialog';
import { Separator } from "../../components/ui/separator";
import { 
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from "../../components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Checkbox } from "../../components/ui/checkbox";

interface Employee {
  id: string;
  employeeId: string;
  name: string;
  department: string;
  designation: string;
  email: string;
  joiningDate: string;
  status: string;
}

interface PayslipRecord {
  id: string;
  employeeId: string;
  name: string;
  department: string;
  designation: string;
  month: string;
  year: string;
  basic: number;
  hra: number;
  da: number;
  specialAllowance: number;
  reimbursements: number;
  pf: number;
  tds: number;
  professionalTax: number;
  otherDeductions: number;
  netSalary: number;
  status: 'draft' | 'generated' | 'approved' | 'paid';
  generatedDate: string;
}

interface SalaryStructure {
  id: string;
  name: string;
  department: string;
  baseSalary: number;
  hraPercentage: number;
  daPercentage: number;
  specialAllowancePercentage: number;
  pfPercentage: number;
  tdsPercentage: number;
  professionalTax: number;
}

// Generate sample data for employees
const generateEmployees = (): Employee[] => {
  const departments = ['Faculty', 'Administration', 'Technical', 'Marketing', 'HR'];
  const designations = [
    'Python Instructor', 'MERN Stack Instructor', 'Java Instructor', 
    'Office Admin', 'System Administrator', 'Digital Marketing Specialist',
    'HR Executive', 'Course Coordinator', 'Placement Officer'
  ];
  
  const employees: Employee[] = [];
  for (let i = 1; i <= 20; i++) {
    const department = departments[Math.floor(Math.random() * departments.length)];
    const designation = designations[Math.floor(Math.random() * designations.length)];
    
    const joinDate = new Date();
    joinDate.setMonth(joinDate.getMonth() - Math.floor(Math.random() * 24));
    
    employees.push({
      id: `${i}`,
      employeeId: `EMP${(1000 + i).toString().padStart(5, '0')}`,
      name: `Employee ${i}`,
      department,
      designation,
      email: `employee${i}@bytesplash.edu`,
      joiningDate: joinDate.toISOString().split('T')[0],
      status: Math.random() > 0.1 ? 'active' : 'inactive'
    });
  }
  return employees;
};

// Generate salary structures
const generateSalaryStructures = (): SalaryStructure[] => {
  const structures: SalaryStructure[] = [];
  const departments = ['Faculty', 'Administration', 'Technical', 'Marketing', 'HR'];
  
  departments.forEach((department, index) => {
    let baseSalary = 0;
    if (department === 'Faculty') {
      baseSalary = 60000;
    } else if (department === 'Administration') {
      baseSalary = 45000;
    } else if (department === 'Technical') {
      baseSalary = 55000;
    } else if (department === 'Marketing') {
      baseSalary = 50000;
    } else {
      baseSalary = 40000;
    }
    
    structures.push({
      id: `${index + 1}`,
      name: `${department} Standard`,
      department,
      baseSalary,
      hraPercentage: 40,
      daPercentage: 10,
      specialAllowancePercentage: 20,
      pfPercentage: 12,
      tdsPercentage: 10,
      professionalTax: 200
    });
    
    structures.push({
      id: `${index + 6}`,
      name: `${department} Senior`,
      department,
      baseSalary: Math.round(baseSalary * 1.4),
      hraPercentage: 40,
      daPercentage: 15,
      specialAllowancePercentage: 25,
      pfPercentage: 12,
      tdsPercentage: 15,
      professionalTax: 200
    });
  });
  
  return structures;
};

// Generate sample data for payslips
const generatePayslips = (): PayslipRecord[] => {
  const months = [
    'January', 'February', 'March', 'April',
    'May', 'June', 'July', 'August',
    'September', 'October', 'November', 'December'
  ];
  
  const employees = generateEmployees();
  const salaryStructures = generateSalaryStructures();
  
  const currentYear = new Date().getFullYear();
  const years = [currentYear.toString(), (currentYear - 1).toString()];
  
  const records: PayslipRecord[] = [];
  
  // Create payslips for different months and employees
  employees.forEach(employee => {
    // Choose a salary structure that matches the employee's department
    const structuresForDept = salaryStructures.filter(s => s.department === employee.department);
    const structure = structuresForDept[Math.floor(Math.random() * structuresForDept.length)];
    
    // Create payslips for last 3 months
    for (let i = 0; i < 3; i++) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const month = months[date.getMonth()];
      const year = date.getFullYear().toString();
      
      const basic = structure.baseSalary;
      const hra = Math.round(basic * (structure.hraPercentage / 100));
      const da = Math.round(basic * (structure.daPercentage / 100));
      const specialAllowance = Math.round(basic * (structure.specialAllowancePercentage / 100));
      const reimbursements = Math.round(Math.random() * 5000);
      
      const grossSalary = basic + hra + da + specialAllowance + reimbursements;
      
      const pf = Math.round(basic * (structure.pfPercentage / 100));
      const tds = Math.round(grossSalary * (structure.tdsPercentage / 100));
      const professionalTax = structure.professionalTax;
      const otherDeductions = Math.round(Math.random() * 1000);
      
      const totalDeductions = pf + tds + professionalTax + otherDeductions;
      const netSalary = grossSalary - totalDeductions;
      
      // Generate status based on month
      let status: 'draft' | 'generated' | 'approved' | 'paid';
      if (i === 0) {
        status = 'paid';
      } else if (i === 1) {
        status = 'approved';
      } else {
        status = Math.random() > 0.5 ? 'generated' : 'draft';
      }
      
      records.push({
        id: `${employee.id}-${date.getMonth()}-${year}`,
        employeeId: employee.employeeId,
        name: employee.name,
        department: employee.department,
        designation: employee.designation,
        month,
        year,
        basic,
        hra,
        da,
        specialAllowance,
        reimbursements,
        pf,
        tds,
        professionalTax,
        otherDeductions,
        netSalary,
        status,
        generatedDate: new Date().toISOString().split('T')[0]
      });
    }
  });
  
  return records;
};

const EMPLOYEES = generateEmployees();
const SALARY_STRUCTURES = generateSalaryStructures();
const PAYSLIP_DATA = generatePayslips();

const MONTHS = [
  'January', 'February', 'March', 'April',
  'May', 'June', 'July', 'August',
  'September', 'October', 'November', 'December'
];

const YEARS = ['2024', '2023', '2022', '2021', '2020'];

const PayslipDetailsView = ({ 
  payslip, 
  onApprove, 
  onPay 
}: { 
  payslip: PayslipRecord, 
  onApprove?: (id: string) => void,
  onPay?: (id: string) => void
}) => {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-semibold">{payslip.name}</h3>
          <p className="text-gray-500">{payslip.designation} | {payslip.department}</p>
          <p className="text-gray-500">Employee ID: {payslip.employeeId}</p>
        </div>
        <div className="text-right">
          <h3 className="text-lg font-semibold">Salary Slip</h3>
          <p className="text-gray-500">{payslip.month} {payslip.year}</p>
          <Badge className={
            payslip.status === 'paid' ? 'bg-green-100 text-green-800' :
            payslip.status === 'approved' ? 'bg-blue-100 text-blue-800' :
            payslip.status === 'generated' ? 'bg-yellow-100 text-yellow-800' :
            'bg-gray-100 text-gray-800'
          }>
            {payslip.status.charAt(0).toUpperCase() + payslip.status.slice(1)}
          </Badge>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4 border-t border-b py-4">
        <div className="space-y-4">
          <h4 className="font-medium">Earnings</h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Basic Salary</span>
              <span>₹{payslip.basic.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">HRA</span>
              <span>₹{payslip.hra.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Dearness Allowance</span>
              <span>₹{payslip.da.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Special Allowance</span>
              <span>₹{payslip.specialAllowance.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Reimbursements</span>
              <span>₹{payslip.reimbursements.toLocaleString()}</span>
            </div>
          </div>
        </div>
        
        <div className="space-y-4">
          <h4 className="font-medium">Deductions</h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Provident Fund</span>
              <span>₹{payslip.pf.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">TDS</span>
              <span>₹{payslip.tds.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Professional Tax</span>
              <span>₹{payslip.professionalTax.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Other Deductions</span>
              <span>₹{payslip.otherDeductions.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="flex justify-between border-b pb-4">
        <div>
          <span className="text-gray-600">Gross Salary</span>
          <div className="font-semibold">
            ₹{(payslip.basic + payslip.hra + payslip.da + payslip.specialAllowance + payslip.reimbursements).toLocaleString()}
          </div>
        </div>
        <div>
          <span className="text-gray-600">Total Deductions</span>
          <div className="font-semibold">
            ₹{(payslip.pf + payslip.tds + payslip.professionalTax + payslip.otherDeductions).toLocaleString()}
          </div>
        </div>
        <div>
          <span className="text-gray-600">Net Salary</span>
          <div className="font-semibold text-bytesplash-blue">
            ₹{payslip.netSalary.toLocaleString()}
          </div>
        </div>
      </div>
      
      <div className="text-center text-sm text-gray-500">
        This is a computer-generated slip and does not require a signature.
      </div>
      
      {(payslip.status === 'generated' || payslip.status === 'approved') && (
        <div className="flex justify-end space-x-3 pt-2">
          {payslip.status === 'generated' && onApprove && (
            <Button 
              onClick={() => onApprove(payslip.id)}
              variant="outline" 
              className="bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
            >
              <FileText className="mr-2 h-4 w-4" />
              Approve Payslip
            </Button>
          )}
          
          {payslip.status === 'approved' && onPay && (
            <Button 
              onClick={() => onPay(payslip.id)}
              className="bg-green-600 text-white hover:bg-green-700"
            >
              <DollarSign className="mr-2 h-4 w-4" />
              Mark as Paid
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

const GeneratePayslipsDialog = ({ 
  isOpen, 
  onClose, 
  onGenerate
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  onGenerate: (data: any) => void;
}) => {
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<string>(new Date().getFullYear().toString());
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [employeeCommandOpen, setEmployeeCommandOpen] = useState(false);
  
  const activeEmployees = EMPLOYEES.filter(e => e.status === 'active');
  
  useEffect(() => {
    if (selectAll) {
      setSelectedEmployees(activeEmployees.map(e => e.employeeId));
    } else if (selectedEmployees.length === activeEmployees.length) {
      setSelectAll(true);
    }
  }, [selectAll]);
  
  const handleSelectEmployee = (employeeId: string) => {
    if (selectedEmployees.includes(employeeId)) {
      setSelectedEmployees(selectedEmployees.filter(id => id !== employeeId));
      setSelectAll(false);
    } else {
      setSelectedEmployees([...selectedEmployees, employeeId]);
      // Check if all employees are now selected
      if (selectedEmployees.length + 1 === activeEmployees.length) {
        setSelectAll(true);
      }
    }
  };
  
  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedEmployees(activeEmployees.map(e => e.employeeId));
    } else {
      setSelectedEmployees([]);
    }
  };
  
  const handleGenerate = () => {
    if (!selectedMonth || !selectedYear || selectedEmployees.length === 0) {
      return;
    }
    
    onGenerate({
      month: selectedMonth,
      year: selectedYear,
      employees: selectedEmployees
    });
    
    onClose();
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Generate Payslips</DialogTitle>
          <DialogDescription>
            Select month, year and employees to generate payslips for
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="month">Month*</Label>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger id="month">
                  <SelectValue placeholder="Select Month" />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((month) => (
                    <SelectItem key={month} value={month}>{month}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="year">Year*</Label>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger id="year">
                  <SelectValue placeholder="Select Year" />
                </SelectTrigger>
                <SelectContent>
                  {YEARS.map((year) => (
                    <SelectItem key={year} value={year}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Employees*</Label>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="selectAll" 
                  checked={selectAll}
                  onCheckedChange={(checked) => handleSelectAll(checked as boolean)}
                />
                <label 
                  htmlFor="selectAll" 
                  className="text-sm text-gray-500 leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Select All
                </label>
              </div>
            </div>
            
            <Popover open={employeeCommandOpen} onOpenChange={setEmployeeCommandOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={employeeCommandOpen}
                  className="w-full justify-between"
                >
                  {selectedEmployees.length > 0
                    ? `${selectedEmployees.length} employees selected`
                    : "Select employees..."}
                  <ArrowRight className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0">
                <Command>
                  <CommandInput placeholder="Search employee..." />
                  <CommandEmpty>No employee found.</CommandEmpty>
                  <CommandList>
                    <CommandGroup>
                      {activeEmployees.map((employee) => (
                        <CommandItem
                          key={employee.employeeId}
                          onSelect={() => handleSelectEmployee(employee.employeeId)}
                          className="flex items-center space-x-2"
                        >
                          <Checkbox 
                            checked={selectedEmployees.includes(employee.employeeId)} 
                            onCheckedChange={() => handleSelectEmployee(employee.employeeId)}
                            id={`employee-${employee.employeeId}`}
                          />
                          <span>{employee.name}</span>
                          <span className="ml-auto text-xs text-gray-500">{employee.designation}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            
            <div className="text-xs text-gray-500">
              {selectedEmployees.length} out of {activeEmployees.length} employees selected
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleGenerate}
            disabled={!selectedMonth || !selectedYear || selectedEmployees.length === 0}
          >
            Generate Payslips
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const EmployeePayslips = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [monthFilter, setMonthFilter] = useState<string | null>(null);
  const [yearFilter, setYearFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [payslips, setPayslips] = useState<PayslipRecord[]>(PAYSLIP_DATA);
  const [selectedPayslip, setSelectedPayslip] = useState<PayslipRecord | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);
  const { toast } = useToast();
  
  const recordsPerPage = 10;
  
  const filteredRecords = payslips.filter(
    (record) => {
      const matchesSearch = 
        record.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        record.employeeId.toLowerCase().includes(searchQuery.toLowerCase()) ||
        record.department.toLowerCase().includes(searchQuery.toLowerCase()) ||
        record.designation.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesMonth = !monthFilter || record.month === monthFilter;
      const matchesYear = !yearFilter || record.year === yearFilter;
      const matchesStatus = !statusFilter || record.status === statusFilter;
      
      return matchesSearch && matchesMonth && matchesYear && matchesStatus;
    }
  );
  
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(indexOfFirstRecord, indexOfLastRecord);
  
  const paginate = (pageNumber: number) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };
  
  const handleViewPayslip = (payslip: PayslipRecord) => {
    setSelectedPayslip(payslip);
    setIsViewDialogOpen(true);
  };
  
  const handleDownloadPayslip = (payslip: PayslipRecord) => {
    toast({
      title: "Payslip Downloaded",
      description: `${payslip.month} ${payslip.year} payslip for ${payslip.name} downloaded successfully.`
    });
  };
  
  const handleEmailPayslip = (payslip: PayslipRecord) => {
    toast({
      title: "Payslip Emailed",
      description: `${payslip.month} ${payslip.year} payslip was sent to ${payslip.name}'s email.`
    });
  };
  
  const handleApprovePayslip = (payslipId: string) => {
    setPayslips(payslips.map(p => 
      p.id === payslipId ? { ...p, status: 'approved' } : p
    ));
    
    if (selectedPayslip && selectedPayslip.id === payslipId) {
      setSelectedPayslip({ ...selectedPayslip, status: 'approved' });
    }
    
    toast({
      title: "Payslip Approved",
      description: "The payslip has been approved successfully."
    });
  };
  
  const handleMarkAsPaid = (payslipId: string) => {
    setPayslips(payslips.map(p => 
      p.id === payslipId ? { ...p, status: 'paid' } : p
    ));
    
    if (selectedPayslip && selectedPayslip.id === payslipId) {
      setSelectedPayslip({ ...selectedPayslip, status: 'paid' });
    }
    
    toast({
      title: "Payment Processed",
      description: "The payslip has been marked as paid."
    });
  };
  
  const handleGeneratePayslips = (data: any) => {
    const { month, year, employees: employeeIds } = data;
    
    // Get employees data
    const selectedEmployees = EMPLOYEES.filter(e => employeeIds.includes(e.employeeId));
    
    // Create new payslips
    const newPayslips: PayslipRecord[] = [];
    
    selectedEmployees.forEach(employee => {
      // Check if payslip already exists for this employee, month and year
      const existingPayslip = payslips.find(p => 
        p.employeeId === employee.employeeId && 
        p.month === month && 
        p.year === year
      );
      
      if (!existingPayslip) {
        // Find salary structure for this department
        const structuresForDept = SALARY_STRUCTURES.filter(s => s.department === employee.department);
        const structure = structuresForDept[Math.floor(Math.random() * structuresForDept.length)];
        
        const basic = structure.baseSalary;
        const hra = Math.round(basic * (structure.hraPercentage / 100));
        const da = Math.round(basic * (structure.daPercentage / 100));
        const specialAllowance = Math.round(basic * (structure.specialAllowancePercentage / 100));
        const reimbursements = Math.round(Math.random() * 5000);
        
        const grossSalary = basic + hra + da + specialAllowance + reimbursements;
        
        const pf = Math.round(basic * (structure.pfPercentage / 100));
        const tds = Math.round(grossSalary * (structure.tdsPercentage / 100));
        const professionalTax = structure.professionalTax;
        const otherDeductions = Math.round(Math.random() * 1000);
        
        const totalDeductions = pf + tds + professionalTax + otherDeductions;
        const netSalary = grossSalary - totalDeductions;
        
        newPayslips.push({
          id: `new-${Date.now()}-${employee.id}`,
          employeeId: employee.employeeId,
          name: employee.name,
          department: employee.department,
          designation: employee.designation,
          month,
          year,
          basic,
          hra,
          da,
          specialAllowance,
          reimbursements,
          pf,
          tds,
          professionalTax,
          otherDeductions,
          netSalary,
          status: 'generated',
          generatedDate: new Date().toISOString().split('T')[0]
        });
      }
    });
    
    if (newPayslips.length > 0) {
      setPayslips([...newPayslips, ...payslips]);
      
      toast({
        title: "Payslips Generated",
        description: `${newPayslips.length} new payslips have been generated for ${month} ${year}.`
      });
    } else {
      toast({
        title: "No New Payslips",
        description: "Payslips already exist for the selected employees for this period.",
        variant: "destructive"
      });
    }
  };
  
  return (
    <div className="h-full flex flex-col bg-gray-50">
      <Header title="Employee Payslips" subtitle="Generate and manage employee salary slips" />
      
      <div className="flex-1 p-6 animate-fade-in">
        <Card className="shadow-sm">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-6 border-b">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 sm:mb-0">Payslip Management</h2>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                <Input
                  type="search"
                  placeholder="Search by name or ID..."
                  className="pl-8 w-full"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              <Button variant="outline" size="icon" className="h-10 w-10">
                <Filter size={16} />
              </Button>
              
              <Button onClick={() => setIsGenerateDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Generate Payslips
              </Button>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-3 p-4 border-b">
            <Select value={monthFilter || 'All'} onValueChange={(value) => setMonthFilter(value || null)}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by Month" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Months</SelectItem>
                {MONTHS.map((month) => (
                  <SelectItem key={month} value={month}>{month}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={yearFilter || 'All'} onValueChange={(value) => setYearFilter(value || null)}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by Year" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Years</SelectItem>
                {YEARS.map((year) => (
                  <SelectItem key={year} value={year}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={statusFilter || 'All'} onValueChange={(value) => setStatusFilter(value || null)}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="generated">Generated</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Designation</TableHead>
                    <TableHead>Month</TableHead>
                    <TableHead>Year</TableHead>
                    <TableHead>Net Salary</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentRecords.length > 0 ? (
                    currentRecords.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell className="font-medium">{record.employeeId}</TableCell>
                        <TableCell>{record.name}</TableCell>
                        <TableCell>{record.department}</TableCell>
                        <TableCell>{record.designation}</TableCell>
                        <TableCell>{record.month}</TableCell>
                        <TableCell>{record.year}</TableCell>
                        <TableCell>₹{record.netSalary.toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge className={
                            record.status === 'paid' ? 'bg-green-100 text-green-800' :
                            record.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                            record.status === 'generated' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }>
                            {record.status.charAt(0).toUpperCase() + record.status.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end space-x-2">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-8 w-8 p-0"
                              onClick={() => handleViewPayslip(record)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-8 w-8 p-0"
                              onClick={() => handleDownloadPayslip(record)}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-8 w-8 p-0"
                              onClick={() => handleEmailPayslip(record)}
                            >
                              <Mail className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-10">
                        <div className="flex flex-col items-center justify-center text-gray-500">
                          <Search className="h-10 w-10 mb-2" />
                          <p className="text-lg">No payslips found</p>
                          <p className="text-sm">Try adjusting your search filters</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            
            <div className="p-4 flex items-center justify-between border-t">
              <div className="text-sm text-gray-500">
                Showing <span className="font-medium">{indexOfFirstRecord + 1}</span> to{" "}
                <span className="font-medium">
                  {Math.min(indexOfLastRecord, filteredRecords.length)}
                </span>{" "}
                of <span className="font-medium">{filteredRecords.length}</span> payslips
              </div>
              
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      onClick={() => paginate(currentPage - 1)} 
                      className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum = currentPage;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <PaginationItem key={i}>
                        <PaginationLink 
                          isActive={pageNum === currentPage} 
                          onClick={() => paginate(pageNum)}
                        >
                          {pageNum}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}
                  
                  <PaginationItem>
                    <PaginationNext 
                      onClick={() => paginate(currentPage + 1)} 
                      className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Payslip Detail Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Payslip Details</DialogTitle>
            <DialogDescription>
              {selectedPayslip && `${selectedPayslip.month} ${selectedPayslip.year}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {selectedPayslip && (
              <PayslipDetailsView 
                payslip={selectedPayslip} 
                onApprove={handleApprovePayslip}
                onPay={handleMarkAsPaid}
              />
            )}
          </div>
          
          <DialogFooter>
            <div className="flex justify-between w-full">
              <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>Close</Button>
              <div className="flex space-x-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    if (selectedPayslip) {
                      handleEmailPayslip(selectedPayslip);
                    }
                  }}
                >
                  <Mail className="mr-2 h-4 w-4" />
                  Email
                </Button>
                <Button 
                  onClick={() => {
                    if (selectedPayslip) {
                      handleDownloadPayslip(selectedPayslip);
                    }
                  }}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Generate Payslips Dialog */}
      <GeneratePayslipsDialog 
        isOpen={isGenerateDialogOpen}
        onClose={() => setIsGenerateDialogOpen(false)}
        onGenerate={handleGeneratePayslips}
      />
    </div>
  );
};

export default EmployeePayslips;
